/**
 * Gardes d'accès pour les features (permissions granulaires)
 *
 * Lecture synchrone depuis le session store. Pas de loader,
 * pas d'appel API : les données sont garanties READY par SessionProvider.
 *
 * @module FeatureAccessGuard
 * @author Équipe Brydge
 * @since 3.0.0
 */

import PropTypes from 'prop-types';
import {usePermissions} from 'packages/common/hooks/session';
import View403 from 'apps/dashboard/pages/error/view/403View';

/**
 * Garde d'accès pour une feature spécifique
 *
 * Protège le rendu des composants enfants en vérifiant si l'utilisateur
 * a accès à la feature requise. Affiche une page 403 si l'accès est refusé.
 *
 * @param {Object} props - Propriétés du composant
 * @param {string} props.feature - Nom de la feature requise
 * @param {React.ReactNode} props.children - Contenu à afficher si l'accès est autorisé
 * @return {React.ReactNode} Contenu protégé ou page 403
 */
const FeatureAccessGuard = ({feature, children}) => {
  const {hasFeatureAccess} = usePermissions();

  if (hasFeatureAccess(feature)) {
    return children;
  }

  return <View403 />;
};

FeatureAccessGuard.propTypes = {
  feature: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

/**
 * Garde d'accès pour plusieurs features (logique OR)
 *
 * Autorise l'accès si l'utilisateur a au moins une des features listées.
 * Affiche une page 403 si aucune des features n'est accessible.
 *
 * @param {Object} props - Propriétés du composant
 * @param {string[]} props.features - Liste des features (logique OR)
 * @param {React.ReactNode} props.children - Contenu à afficher si l'accès est autorisé
 * @return {React.ReactNode} Contenu protégé ou page 403
 */
export const FeatureAccessGuardAny = ({features, children}) => {
  const {hasAnyFeatureAccess} = usePermissions();

  if (hasAnyFeatureAccess(features)) {
    return children;
  }

  return <View403 />;
};

FeatureAccessGuardAny.propTypes = {
  features: PropTypes.arrayOf(PropTypes.string).isRequired,
  children: PropTypes.node.isRequired,
};

export default FeatureAccessGuard;
